/* eslint-disable max-lines-per-function */
/* eslint-disable newline-per-chained-call */
import EventBus from '@mvps-genesis/event-bus';
import {AnticipatedShipDate} from 'custom-extensions/anticipated-ship-date';
import {BopisController} from 'custom-extensions/bopis';
import BopisPickupTimeController from 'custom-extensions/bopis/lib/elements/BopisPickupTime/BopisPickupTimeController';
import {FormProxy} from 'custom-extensions/form-proxy-mixin';
import {LitElement, html, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import {createRef, ref} from 'lit/directives/ref.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import styles from './ProductBopisSelection.component.scss?as=stylesheet';

// eslint-disable-next-line new-cap
export default class ProductBopisSelection extends FormProxy(LitElement) {

	/**
	 * Define customized shadow root options.
	 * @see https://lit.dev/docs/components/shadow-dom/#setting-shadowrootoptions
	 * @static
	 */
	static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true
	};

	static get styles () {
		return [styles];
	}

	/**
	 * Define reflector properties
	 * @see https://lit.dev/docs/components/properties/
	 * @static
	 */
	static get properties () {
		return {
			formattedPickupHours: {
				type: String
			},
			productfields: {
				type: Object
			},
			selectedStore: {
				type: Object
			}
		};
	};

	/**
	 * Used to store a reference to the BOPIS Option
	 * @private
	 * @type {Object}
	 */
	bopisCheckbox = createRef();

	/**
	 * @constructor
	 */
	constructor () {
		super();
		this.controller = BopisController;
		this.formattedPickupHours = '';
		this.productfields = {};
		this.selectedStore = {};
		this.storeLoaded = false;

		this.modalID = 'js-bopis-product';
		this.modal = document.getElementById(this.modalID);
		this.modalOpen = false;

		this.timeZone = 'America/Chicago';
		this.bopisDrawer = this.modal.querySelector('x-bopis-drawer');

		this.bopisPickupTimeController = new BopisPickupTimeController(this);
	}

	firstUpdated () {
		this.productfields = window?.mivaJS?.BopisProductFields || {};
	}

	updated (changedProperties) {
		super.updated();

		/* Push currently selected store to the drawer */
		this.bopisDrawer?.setAttribute?.('basketBopisStore', JSON.stringify(this.bopisStoreSelected || {}));

		if (changedProperties.has('selectedStore')) {
			this.bopisPickupTimeController.init();
		}

	}

	/**
	 * Invoked when a component is added to the document's DOM.
	 * @see https://lit.dev/docs/components/lifecycle/#connectedcallback
	 * @static
	 */
	async connectedCallback () {
		super.connectedCallback();
		this.bopisStoreUnsubscribe = this.controller.store.subscribe(() => this.requestUpdate());

		this.modal.addEventListener('close', this.handleModalClose);
		EventBus.addEventListener('MVPS:productVariantChanged', this.productVariantChanged.bind(this), false);
		EventBus.addEventListener('MVPS:shopStoreProductChanged', this.storeSelected.bind(this), false);

		await this.bopisPickupTimeController.init();

		// Refresh every minute
		this.interval = setInterval(() => {
			this.bopisPickupTimeController.determinePickupTime();
		}, 60000);
	}

	/**
	 * Invoked when a component is removed from the document's DOM.
	 * @see https://lit.dev/docs/components/lifecycle/#disconnectedcallback
	 * @static
	 */
	disconnectedCallback () {
		this.bopisStoreUnsubscribe();
		this.modal.removeEventListener('close', this.handleModalClose);
		EventBus.removeEventListener('MVPS:productVariantChanged', this.productVariantChanged.bind(this), false);
		EventBus.removeEventListener('MVPS:shopStoreProductChanged', this.storeSelected.bind(this), false);

		clearInterval(this.interval);

		super.disconnectedCallback();
	}

	/**
	 * @see https://lit.dev/docs/components/rendering/
	 */
	render () {
		const classes = {
			'g-product-bopis__store': true,
			'g-product-bopis__store-disabled': this.bopisUnavailable
		};

		return html`
			<form class="g-product-bopis">
				${this.drawShippingOption()}
				<div class="${classMap(classes)}">
					<label class="g-product-bopis__option g-form-radio" @click="${(e)=> this.bopisLabelClicked(e, 'store')}">
						<input	class="g-form-checkbox__input"
								type="radio"
								name="Bopis_Selection"
								.value="${this.bopisStoreSelected?.identifier ? this.bopisStoreSelected.identifier : 0}"
								.checked="${this.defaultOptionChecked() === 'bopis'}"
								?disabled="${this.bopisUnavailable}"
								@change="${(e) => this.bopisOptionChanged(e, 'store')}"
								${ref(this.bopisCheckbox)}
						>
						<span class="g-product-bopis__caption">
							<span class="g-product-bopis__caption-inner">
								<span class="u-icon-pickup g-product-bopis__store-icon" aria-hidden="true"></span>
								<span class="g-product-bopis__store-prompt">
									<span class="g-product-bopis__store-prompt-text">
										${this.drawBopisTitle()}
									</span>
								</span>
								<span class="g-product-bopis__store-text">
									${this.drawStoreCTA()}
								</span>
								${this.drawAvailabilityBopis()}
							</span>
						</span>
					</label>
				</div>
			</form>
		`;
	}

	/**
	 * Draws out elements for the shipping option. If `bopis_only`, it's omitted.
	 * @return {html}
	 */
	drawShippingOption () {
		let shippingMessage = '';
		let leadTimeMessage = '';
		if (this.productfields?.bopis_only) {
			return nothing;
		}

		if (this.productfields?.dropship) {
			shippingMessage = html`<span class="g-product-bopis__shipping-text">This is a <strong>manufacturer direct</strong> item`;
		} else if (this.productfields?.item_lead_time && !this.productfields?.anticipated_ship_date) {
			shippingMessage = html`<span class="g-product-bopis__shipping-text">Ships in: ${this.productfields?.item_lead_time} business day${this.productfields?.item_lead_time > 1 ? 's' : nothing}`;
		}

		if (this.productfields?.item_lead_time || this.productfields?.anticipated_ship_date) {
			leadTimeMessage = html`<span class="g-product-bopis__days-to-ship">${this.formattedShipsBy()}</span>`;
		}

		return html`
			<div class="g-product-bopis__shipping">
				<label class="g-product-bopis__option g-form-radio" @click="${(e)=> this.bopisLabelClicked(e, 'shipping')}">
					<input	class="g-form-checkbox__input"
							type="radio"
							name="Bopis_Selection"
							value=""
							?disabled="${this.productfields?.bopis_only}"
							.checked="${this.defaultOptionChecked() === 'shipping'}"
							@change="${(e) => this.bopisOptionChanged(e, 'shipping')}"
					>
					<span class="g-product-bopis__caption">
						<span class="g-product-bopis__caption-inner">
							<span class="u-icon-truck g-product-bopis__shipping-icon" aria-hidden="true"></span>
							<span class="g-product-bopis__shipping-prompt">Ship Item</span>
							${shippingMessage}
							${leadTimeMessage}
							${this.drawAvailabilityShipping()}
						</span>
					</span>
				</label>
			</div>
		`;
	}

	get storesAvailable () {
		// Check if there is any in stock in stores.
		let availableStoresCount = 0;
		// eslint-disable-next-line array-element-newline
		for (const [store, value] of Object.entries(this.productfields?.bopis_inventory || {})) {
			if (!this.controller?.api?.identifierIsAllowed(store)) {
				continue;
			}

			if (value < 1) {
				continue;
			}

			availableStoresCount++;
		}

		return availableStoresCount;
	}

	/**
	 * Draws out elements for the Store Name.
	 * @return {html}
	 */
	drawStoreName () {
		let formattedTime = '';
		if (this.bopisStoreSelected?.productInventory && this.formattedPickupHours) {
			formattedTime = unsafeHTML(`${this.formattedPickupHours} at `);
		}

		if (this.bopisStoreSelected?.productInventory && this.storesAvailable > 1) {
			return html`Available ${formattedTime ? html`${formattedTime}` : 'in'} <span class="g-product-bopis__store-name-highlight">${this.bopisStoreSelected.name}</span> and <span class="g-product-bopis__store-name-highlight">${this.storesAvailable - 1} other location${this.storesAvailable > 2 ? 's' : nothing}</span>`;
		} else if (this.bopisStoreSelected?.productInventory) {
			return html`Available ${formattedTime ? html`${formattedTime}` : 'in'} <span class="g-product-bopis__store-name-highlight">${this.bopisStoreSelected.name}</span>`;
		} else if (this.storesAvailable) {
			return html`Available for Pick Up in <span class="g-product-bopis__store-name-highlight">${this.storesAvailable} other location${this.storesAvailable > 1 ? 's' : nothing}</span>`;
		}

		return nothing;
	}

	drawBopisTitle () {
		if (!this.bopisStoreSelected?.productInventory && this.storesAvailable && !this.bopisUnavailable) {
			return 'Pick Up Nearby';
		}

		return 'Pick Up In-Store';
	}

	get bopisUnavailable () {
		if (this.productfields?.exclude_store_pickup) {
			return true;
		}

		if (!this.bopisStoreSelected?.productInventory && !this.storesAvailable) {
			return true;
		}

		return false;
	}

	/**
	 * Draws out elements for the "Change/ Select Store" CTA.
	 * @return {html}
	 */
	drawStoreCTA () {
		if (this.bopisUnavailable) {
			return nothing;
		}

		return html`
			<span class="g-product-bopis__store-cta">
				<button href="#${this.modalID}" class="g-product-bopis__store-trigger g-button--no-styling" @click="${this.openModal}" aria-haspopup="dialog" aria-expanded="${this.modalOpen}" aria-controls="${this.modalID}">
					${this.drawStoreName()}
				</button>
			</span>
		`;
	}

	/**
	 * Draws out element "availability" flag
	 * @return {html}
	 */
	drawAvailabilityBopis () {
		if (this.productfields?.exclude_store_pickup) {
			return html`<div class="g-product-bopis__availability g-product-bopis__store-unavailable">Currently Not Eligible for Pickup</div>`;
		}

		if (this.bopisUnavailable) {
			return html`<div class="g-product-bopis__availability g-product-bopis__store-unavailable">Current Selection Not Eligible for Pickup</div>`;
		}

		if (this.bopisStoreSelected?.productInventory > 0) {
			return html`<div class="g-product-bopis__availability g-product-bopis__store-instock">In Stock</div>`;
		}

		return nothing;
	}

	/**
	 * Draws out element "availability" flag
	 * @return {html}
	 */
	drawAvailabilityShipping () {
		if (this.productfields?.bopis_only) {
			return html`<div class="g-product-bopis__availability g-product-bopis__store-unavailable">Currently Not Eligible to Ship</div>`;
		}

		if (this.productfields?.handling_fee_message) {
			return html`<div class="g-product-bopis__availability g-product-bopis__store-message">${unsafeHTML(this.productfields.handling_fee_message)}</div>`;
		}

		return nothing;
	}

	get bopisStoreSelected () {
		let store = {};

		if (this.selectedStore?.name) {
			store = this.selectedStore;
		} else if (this.controller?.store?.state?.myStore?.location?.listings_name) {
			store = this.controller.store.state.myStore;
		}

		store.productInventory = 0;

		if (!store?.identifier || !store?.name || !this.productfields?.bopis_inventory) {
			return store;
		}

		this.controller.api.productInventoryStore(this.productfields.bopis_inventory, store, 1);

		return store;
	}


	/**
	 * Handles the click on the button. This will open the modal with the BOPIS Drawer.
	 */
	openModal (e) {
		e.preventDefault();
		this.openModalLowlevel();
	}

	/**
	 * This will open the modal with the BOPIS Drawer.
	 */
	openModalLowlevel () {
		this.modal.open();
		this.modalOpen = true;
	}

	/**
	 * Handles when the modal has been closed
	 */
	handleModalClose = () => {
		// eslint-disable-next-line no-invalid-this
		this.modalOpen = false;
	}

	/**
	 * Updates applicable product details for API calls when a variant has changed.
	 * @param {Event}	e
	 */
	productVariantChanged (e) {
		const payload = e.detail;
		const productData = payload.productData?.variant;

		let inventory = productData?.customfield_values?.bopis_inventory;
		if (!inventory || inventory === '') {
			inventory = {};
		} else {
			inventory = JSON.parse(inventory);
		}

		// Keeping fields from the parent product
		const fields = {...this.productfields};

		// eslint-disable-next-line camelcase
		fields.bopis_inventory = inventory;

		// eslint-disable-next-line camelcase
		fields.drop_ship_item = productData?.customfield_values?.drop_ship_item;

		// eslint-disable-next-line camelcase
		fields.handling_fee_message = productData?.customfield_values?.extra_shipping_cost || fields.handling_fee_message;

		this.productfields = fields;
	}

	/**
	 * Handles when a BOPIS option has changed. Depending on the type it may trigger the Drawer to open.
	 * @param {Event}	e
	 * @param {String}	type
	 */
	bopisOptionChanged (e, type) {
		this.requestUpdate();

		if (type === 'store' && !this.bopisStoreSelected?.identifier) {
			this.handleStoreSelectedNoStore(e);
		}
	}

	/**
	 * Handles when a store is required, but a store is missing.
	 * @param {Event}	e
	 */
	handleStoreSelectedNoStore (e) {
		e.preventDefault();
		e.stopPropagation();

		this.openModalLowlevel();
	}

	/**
	 * Handles when the BOPIS Label is clicked.
	 * @param {Event}	e
	 * @param {String}	type
	 */
	bopisLabelClicked (e, type) {
		if (this.productfields?.exclude_store_pickup) {
			return;
		}

		if (type === 'store' && !this.bopisStoreSelected?.identifier) {
			this.handleStoreSelectedNoStore(e);
		}
	}

	/**
	 * Handles the default Option that is checked
	 */
	defaultOptionChecked () {
		if (this.productfields?.bopis_only && !this.bopisUnavailable) {
			return 'bopis';
		}

		return 'shipping';
	}

	/**
	 * Handles the "Ships on" (`anticipated_ship_date`) or "Ships on or before" (`item_lead_time`)
	 */
	formattedShipsBy () {
		const shipsBy = new AnticipatedShipDate({
			anticipatedShipDate: this.productfields?.anticipated_ship_date,
			leadTime: this.productfields.item_lead_time
		}).get();

		if (this.productfields?.anticipated_ship_date) {
			return html`
				Ships on<br>
				<strong>${shipsBy.isValid ? shipsBy.toFormat('EEEE, MMMM d') : this.productfields.anticipated_ship_date}</strong>
			`;
		}

		return html`
			Expected to ship on or before<br>
			<strong>${shipsBy.toFormat('EEEE, MMMM d')}</strong>
		`;
	}

	/*
	 * Handles when the "Pick Up Here" is selected in the modal
	 * @param {Event}	e
	 */
	storeSelected (e) {
		if (!e?.detail?.location?.identifier) {
			return;
		}

		this.selectedStore = e.detail.location;
		this.modal.close();

		/* Auto check the option since we just had the user select it */
		if (this.bopisCheckbox?.value && !this.bopisCheckbox?.value?.checked) {
			this.bopisCheckbox.value.checked = true;
		}
	}

};

customElements.define('x-product-bopis', ProductBopisSelection);
